<script lang="ts">
	import Container from '@components/global/Container.svelte';
	import Modal from '@components/global/Modal.svelte';
	import RoundCheck from 'virtual:icons/ic/round-check';
	import RoundShare from 'virtual:icons/ic/round-share';
	import avatarImg from '@lib/images/placeholders/avatar.png?enhanced';
	import { page } from '$app/stores';
	import CopyOutline from 'virtual:icons/ion/copy-outline';
	import { toast } from 'svelte-sonner';
	import XIcon from '@lib/images/icons/social-media/x.svg';
	import facebookIcon from '@lib/images/icons/social-media/facebook.svg';
	import whatsAppIcon from '@lib/images/icons/social-media/whatsapp.svg';
	import lineIcon from '@lib/images/icons/social-media/line.svg';
	import { truncate } from 'lodash-es';
	import CenterNotify from '../global/micro/CenterNotify.svelte';
	import type { Writer, Publisher } from '@lib/types';

	type Profile = Writer | Publisher;

	export let profile: Profile;

	let descriptionModalEl: HTMLDialogElement,
		shareModalEl: HTMLDialogElement,
		isCopyLink = false;

	const profileType = $page.url.pathname.split('/')[1];
	const profileUrl = `${$page.url.origin}/${profileType}/${profile?.username}`;

	function copyProfileLink() {
		try {
			isCopyLink = true;
			navigator.clipboard.writeText(profileUrl);
			setTimeout(() => (isCopyLink = false), 3000);
		} catch (error) {
			console.error(error);
			toast.error('Terjadi kesalahan saat menyalin');
		}
	}

	const textTemplate = 'Lorem ipsum dolor';
	const sharePlatforms = [
		{
			label: 'WhatsApp',
			url: `https://api.whatsapp.com/send?text=${encodeURIComponent(textTemplate)}`,
			icon: whatsAppIcon
		},
		{
			label: 'X',
			url: `https://x.com/intent/tweet?text=${encodeURIComponent(textTemplate)}`,
			icon: XIcon
		},
		{
			label: 'Line',
			url: `https://lineit.line.me/share/ui?url=${encodeURIComponent(textTemplate)}`,
			icon: lineIcon
		},
		{
			label: 'Facebook',
			url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(textTemplate)}`,
			icon: facebookIcon
		}
	];

	const description = profile?.description ?? 'Tidak ada deskripsi';
	const DESCRIPTION_LIMIT = 150;
</script>

<div
	class="grid min-h-56 place-items-center border-b border-dashed bg-base-200/30 py-6 pre-lg:min-h-60"
>
	<Container>
		<div class="mx-auto flex flex-col items-center gap-5 pre-sm:flex-row pre-sm:gap-8">
			<div
				class="aspect-square size-32 overflow-hidden rounded-full border border-dashed border-gray-300 object-cover object-center pre-sm:size-36 pre-lg:size-44"
			>
				{#if profile.image_url}
					<img class="size-full" src={profile.image_url} alt={profile.name} />
				{:else}
					<enhanced:img class="size-full" src={avatarImg} alt={profile.name} />
				{/if}
			</div>
			<div class="flex-1 space-y-1.5 text-center pre-sm:text-left">
				<div class="flex flex-wrap items-center justify-center gap-3 pre-sm:justify-start">
					<p class="text-2xl font-bold pre-sm:text-3xl pre-lg:text-4xl">{profile.name}</p>
					<button
						on:click|stopPropagation={() => shareModalEl.showModal()}
						type="button"
						class="btn btn-sm bg-base-100 hover:bg-base-100"
						aria-label="Bagikan"
						title="Bagikan"
					>
						<RoundShare />
					</button>
					<Modal on:clickoutside={() => shareModalEl.close()} bind:modalEl={shareModalEl}>
						<p class="text-left text-lg font-semibold">Bagikan Profil</p>
						<div class="mt-4 grid grid-cols-2 gap-3">
							{#each sharePlatforms as platform}
								<a
									class="flex items-center gap-2 rounded-lg border border-gray-300 p-3 text-sm font-medium hover:bg-base-200/30"
									href={platform.url}
									target="_blank"
									rel="noreferrer noopener"
								>
									<img class="size-6" src={platform.icon} alt={platform.label} />
									{platform.label}
								</a>
							{/each}
						</div>
						<button
							disabled={isCopyLink}
							on:click={copyProfileLink}
							type="button"
							class="mt-4 flex min-h-12 w-full items-center justify-between rounded-md bg-base-200/30 p-3 text-left disabled:cursor-not-allowed"
							title="Salin ke Clipboard"
						>
							<span class="text-xs"> {profileUrl} </span>
							{#if isCopyLink}
								<RoundCheck class="text-success" />
							{:else}
								<CopyOutline class="text-lg" />
							{/if}
						</button>
						<CenterNotify isShow={isCopyLink} message="Link berhasil disalin" />
					</Modal>
				</div>
				<p class="text-balance text-sm text-gray-600 md:text-base">
					{truncate(description, { length: DESCRIPTION_LIMIT })}
				</p>
				{#if description.length > DESCRIPTION_LIMIT}
					<button
						on:click={() => descriptionModalEl.showModal()}
						type="button"
						class="link-hover link text-sm font-medium text-secondary md:text-base"
						>Selengkapnya</button
					>
				{/if}
				<Modal bind:modalEl={descriptionModalEl}>
					<div class="text-left">
						<p class="text-xl font-semibold">Deskripsi</p>
						<div class="mt-4 whitespace-pre-wrap text-sm">
							{description}
						</div>
					</div>
				</Modal>
			</div>
		</div>
	</Container>
</div>
